import React, { FC, useContext, useMemo } from 'react';

import NestedSpinner from '../../../../components/loading/nestedSpinner';
import styled from 'styled-components';

// constants
import aampeTheme from '../../../../constants/aampeTheme';

// context
import { EditorContext } from '../../../../context/EditorContext';

// models
import { WebPushPreviewModel } from '../../../../models/editor.model';
import { PreviewComponentProps } from '../../../../models/formula.model';

import AddVariationMsg from '../shared/AddVariationMsg';
import GetStartedMsg from '../shared/GetStartedMsg';
import PreviewError from '../shared/PreviewError';
// components
import FakeMenuBar from './FakeMenuBar';

const WebPushMain: FC<
    React.PropsWithChildren<PreviewComponentProps<WebPushPreviewModel>>
> = ({ randomSample, formulaHasVariant }) => {
    const {
        editorDetails: { isGenerating, isPreviewError, previewError, previews },
    } = useContext(EditorContext);

    const hasText = useMemo(() => {
        const sample = previews?.[0] as any;
        return sample?.content?.body || sample?.content?.header;
    }, [previews]);

    return (
        <Container>
            <FakeMenuBar />
            <Content>
                {isGenerating && <NestedSpinner />}
                {!isGenerating && isPreviewError && (
                    <PreviewError message={previewError} />
                )}
                {!isGenerating && !isPreviewError && !hasText && (
                    <GetStartedMsg />
                )}
                {!isGenerating &&
                    !isPreviewError &&
                    hasText &&
                    !formulaHasVariant && <AddVariationMsg />}
                {!isGenerating && !isPreviewError && hasText && (
                    <Messages>
                        {randomSample.map((preview, i) => (
                            <Message key={i + preview.id}>
                                <Header>{preview.content.header}</Header>
                                <Body>{preview.content.body}</Body>
                            </Message>
                        ))}
                    </Messages>
                )}
            </Content>
        </Container>
    );
};

export default WebPushMain;

const Container = styled.div({
    display: 'flex',
    flexFlow: 'column nowrap',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    height: '450px',
    width: '400px',
    minWidth: '320px',
    backgroundColor: aampeTheme.colors.neutral09,
    borderWidth: '4px 4px 0 0',
    borderStyle: 'solid',
    borderColor: aampeTheme.colors.textAccessibleWG01,
    borderRadius: '0 16px 0 0',
});

const Content = styled.div({
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'flex-end',
    alignItems: 'end',
    margin: aampeTheme.spacing(2, 1, 2, 0),
    gap: aampeTheme.spacing(1),
});

const Messages = styled.div({
    display: 'flex',
    flexFlow: 'column nowrap',
    justifyContent: 'flex-start',
    alignItems: 'end',
    gap: aampeTheme.spacing(1),
    overflowY: 'auto',
});

const Message = styled.div({
    backgroundColor: aampeTheme.colors.bordersWG01,
    padding: aampeTheme.spacing(2),
    borderRadius: '16px',
    display: 'flex',
    flexFlow: 'column nowrap',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    color: aampeTheme.colors.secondary500,
    width: '300px',
    boxShadow:
        '0px 3.29008px 6.58015px -1.64504px rgba(16, 24, 40, 0.1), 0px 1.64504px 3.29008px -1.64504px rgba(16, 24, 40, 0.06)',
    gap: aampeTheme.spacing(1),
    whiteSpace: 'pre-wrap',
});

const Header = styled.div({
    fontSize: '16px',
    lineHeight: '16px',
    color: aampeTheme.colors.textAccessibleWG01,
    fontWeight: 400,
});

const Body = styled.div({
    fontSize: '14px',
    lineHeigh: '16px',
    color: aampeTheme.colors.gray600,
    fontWeight: 400,
});
